import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const isRTL = i18n.language?.startsWith("ar");
  const phoneDisplay = t("footer.phone");
  const phoneHref = `tel:${phoneDisplay.replace(/[^+\d]/g, "")}`;
  const mailchimpAction =
    "https://arafouf.us10.list-manage.com/subscribe/post?u=27c69faeef13513e1715be5f2&id=980fba3e29&f_id=005f49e4f0";
  const mailchimpHoneypotName = "b_27c69faeef13513e1715be5f2_980fba3e29";

  return (
    <footer className="relative w-full mt-0 overflow-hidden rounded-t-[32px] bg-[#151311] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 right-1/4 h-56 w-56 rounded-full bg-brand/20 blur-3xl" />
        <div className="absolute -bottom-20 left-8 h-48 w-48 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div ref={ref} className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-10 backdrop-blur-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/assets/logos/logo_abarj.png"
                  alt={t("footer.brand")}
                  className="h-12 w-auto object-contain"
                  loading="lazy"
                />
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {t("footer.brand")}
                  </h3>
                  <p className="text-sm md:text-base text-stone-300">
                    {t("footer.tagline")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <a
                  href="https://www.tiktok.com/@rfof18"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 bg-white/5 text-stone-300 hover:border-brand hover:bg-brand hover:text-white transition-colors cursor-pointer"
                  aria-label="TikTok"
                >
                  <i className="ri-tiktok-line text-lg" />
                </a>
                <a
                  href="https://x.com/arafoufsa"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 bg-white/5 text-stone-300 hover:border-brand hover:bg-brand hover:text-white transition-colors cursor-pointer"
                  aria-label="X / Twitter"
                >
                  <i className="ri-twitter-x-line text-lg" />
                </a>
                <a
                  href="https://www.instagram.com/arafoufsa"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-white/15 bg-white/5 text-stone-300 hover:border-brand hover:bg-brand hover:text-white transition-colors cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-lg" />
                </a>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-4 md:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-300 mb-3">
                  {t("footer.newsletterTitle")}
                </p>
                <form action={mailchimpAction} method="post" target="_blank" noValidate>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      name="EMAIL"
                      id="mce-EMAIL-footer"
                      placeholder={t("footer.newsletterPlaceholder")}
                      className="flex-1 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand/60 transition-all"
                      required
                    />
                    <div aria-hidden="true" className="absolute -left-[5000px]">
                      <input type="text" name={mailchimpHoneypotName} tabIndex={-1} defaultValue="" />
                    </div>
                    <motion.button
                      type="submit"
                      name="subscribe"
                      className="inline-flex items-center justify-center gap-2 bg-brand text-white text-sm font-medium px-5 py-3 rounded-xl hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t("footer.newsletterButton")}
                      <i className={`ri-${isRTL ? "arrow-left-line" : "arrow-right-line"} text-sm`} />
                    </motion.button>
                  </div>
                  <p className="text-xs text-stone-400 mt-3">
                    {isRTL
                      ? "سيتم فتح صفحة تأكيد الاشتراك في نافذة جديدة."
                      : "A confirmation page will open in a new tab."}
                  </p>
                </form>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-3 rounded-2xl border border-white/10 bg-black/20 p-5 md:p-6"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h4 className="text-xs font-semibold text-white uppercase tracking-[0.14em] mb-5">
                {t("footer.solutionsTitle")}
              </h4>
              <ul className="space-y-3">
                {[
                  { label: t("footer.warehouseRacking"), href: "/services" },
                  { label: t("footer.retailShelving"), href: "/services" },
                  { label: t("footer.supermarketSystems"), href: "/services" },
                  { label: t("footer.customProjects"), href: "/contact-us" },
                ].map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? 20 : -20 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.08 }}
                  >
                    <a
                      href={link.href}
                      className="group flex items-center justify-between gap-3 text-sm md:text-base text-stone-300 hover:text-white transition-colors"
                    >
                      <span>{link.label}</span>
                      <i className={`ri-${isRTL ? "arrow-left-s-line" : "arrow-right-s-line"} text-brand opacity-70 group-hover:opacity-100`} />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="lg:col-span-4 rounded-2xl border border-white/10 bg-black/20 p-5 md:p-6"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <h4 className="text-xs font-semibold text-white uppercase tracking-[0.14em] mb-5">
                {t("footer.contactTitle")}
              </h4>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-brand">
                    <i className="ri-phone-line text-base" />
                  </span>
                  <a
                    href={phoneHref}
                    dir="ltr"
                    className="inline-block text-sm md:text-base text-stone-300 leading-relaxed hover:text-white transition-colors"
                    style={{ unicodeBidi: "isolate" }}
                  >
                    {phoneDisplay}
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-brand">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <p className="text-sm md:text-base text-stone-300 leading-relaxed">{t("footer.email")}</p>
                </div>
              </div>

              <h4 className="text-xs font-semibold text-white uppercase tracking-[0.14em] mb-4">
                {t("footer.locationTitle")}
              </h4>
              <div className="space-y-1">
                <p className="text-sm md:text-base text-stone-300 leading-relaxed">{t("footer.address1")}</p>
                <p className="text-sm md:text-base text-stone-400 leading-relaxed">{t("footer.address2")}</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="mt-10 pt-6 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-stone-400">
                &copy; {t("footer.copyright")}
              </p>
              <div className="flex items-center gap-5">
                <a href="/privacy-policy" className="text-sm text-stone-400 hover:text-white transition-colors underline underline-offset-4 cursor-pointer">
                  {t("footer.privacy")}
                </a>
                <a href="/terms-of-service" className="text-sm text-stone-400 hover:text-white transition-colors underline underline-offset-4 cursor-pointer">
                  {t("footer.terms")}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}